import { Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';

import { ReplaySubject } from 'rxjs';

import { distinctUntilChanged, switchMap, take } from 'rxjs/operators';

import { CHAT_STORE, IChatStore } from '@sondermind/data-access/chat';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { LegacyNativeBinding } from '@sondermind/data-access/legacy-native';
import { ClientUser } from '@sondermind/utilities/models-user-client';
import { SONDERMIND_WEB_APP } from '@sondermind/utilities/native-binding';
import { FeatureFlags, Flags, IdleDetectionService, STORAGE } from '@sondermind/utilities/services';

import { DeeplinkMapper } from './deeplink-mapping';
import { BEIGE_BACKGROUND_CLASSES, WHITE_BACKGROUND_CLASSES } from './route-data.helpers';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  constructor(
    private currentUserService: CurrentUser,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private idleDetectionService: IdleDetectionService,
    private location: Location,
    private deeplinker: DeeplinkMapper,
    private featureFlags: FeatureFlags,

    @Inject(STORAGE)
    private storage: Storage,

    @Inject(CHAT_STORE)
    private chatStore: IChatStore,

    @Inject(SONDERMIND_WEB_APP)
    private native: LegacyNativeBinding
  ) {}

  private initializedSubj = new ReplaySubject<boolean>(1);
  private readySubj = new ReplaySubject<boolean>(1);
  private routeDataSubj = new ReplaySubject<any>(1);
  private scrollToTopSubj = new ReplaySubject<boolean>(1);
  private subnavHiddenSubj = new ReplaySubject<boolean>(1);
  initialized$ = this.initializedSubj.asObservable();

  ready$ = this.initialized$.pipe(switchMap(() => this.readySubj.asObservable()));
  routeData$ = this.routeDataSubj.asObservable();
  isScrollToTop$ = this.scrollToTopSubj.asObservable();
  subnavHidden$ = this.subnavHiddenSubj.asObservable();

  initialize(): void {
    // check that we're able to get a browser storage location
    // if none of them are working, we're likely in private mode
    if (this.storage == null) {
      this.router.navigateByUrl('/error/private');
      this.initializedSubj.next(true);
      return;
    }

    // initialize the idle timer
    this.idleDetectionService.initialize(1770, 30, this.handleIdleTimeout.bind(this));

    // watch router events, and roll up routedata
    this.router.events
      .subscribe(this.handleRouteChanged.bind(this));

    // if the window has a native delegate registration, bind to that
    this.native.initializeAndBind();

    // watch the current user for metrics
    this.initialized$
      .pipe(
        switchMap(() => this.currentUserService.user$),
        distinctUntilChanged()
      )
      .subscribe((user) => {
        this.handleAnalyticsUserChanged(user);
        this.registerFeatureFlags(user);
        if (user) {
          this.chatStore.initializeChat$().subscribe();
        } else {
          this.chatStore.teardownChat$().subscribe();
        }
      });

    // update the path depending on deeplink mapping
    this.handleDeeplinkRedirect();

    // now get moving on the rest of stuff
    this.currentUserService.initialize$()
      .subscribe(() => {
        this.initializedSubj.next(true);
        this.router.initialNavigation();
        this.idleDetectionService.resetIdleDetection();
      });
  }

  ready(): void {
    this.readySubj.next(true);
  }

  scrollToTop(): void {
    this.scrollToTopSubj.next(true);
  }

  setSubnavHidden(hidden: boolean): void {
    this.subnavHiddenSubj.next(hidden);
  }

  updateRouteData(updates: Record<string, unknown>): void {
    // Get current route data and merge with updates
    this.routeData$.pipe(take(1)).subscribe((currentData) => {
      const updatedData = { ...currentData, ...updates };
      this.routeDataSubj.next(updatedData);
    });
  }

  setWhiteBackground(): void {
    this.routeData$.pipe(take(1)).subscribe((currentData) => {
      const existingClasses = currentData?.additionalContainerClasses || {};
      this.updateRouteData({
        additionalContainerClasses: { ...existingClasses, ...WHITE_BACKGROUND_CLASSES }
      });
    });
  }

  setBeigeBackground(): void {
    this.routeData$.pipe(take(1)).subscribe((currentData) => {
      const existingClasses = currentData?.additionalContainerClasses || {};
      this.updateRouteData({
        additionalContainerClasses: { ...existingClasses, ...BEIGE_BACKGROUND_CLASSES }
      });
    });
  }

  setTitle(title: string | null): void {
    // eslint-disable-next-line no-param-reassign
    title = title ? `${title} - SonderMind Portal` : 'SonderMind Portal';
    this.title.setTitle(title);
  }

  private handleIdleTimeout(): void {
    this.router.navigateByUrl('/logout?idle=true', { skipLocationChange: true });
  }

  private handleRouteChanged(e: Event | RouterEvent): void {
    if (e instanceof NavigationEnd) {
      // roll up data from the route tree
      // further down wins

      const path = [this.route.snapshot];
      let route = path[0];
      while (route.firstChild) {
        route = route.firstChild;
        path.push(route);
      }

      const data = path.reduce((pathData, pathRoute) => ({
        ...pathData, fragment: pathRoute.fragment, ...pathRoute.data
      }), {});
      this.routeDataSubj.next(data);
    }
  }

  private handleDeeplinkRedirect() {
    const redirect = this.deeplinker.mapLocation(this.location.path(true));
    if (redirect) {
      this.location.replaceState(redirect);
    }
  }

  private handleAnalyticsUserChanged(u: ClientUser): void {
    // eslint-disable-next-line prefer-destructuring
    const outbound: any = (<any>window).outbound;
    if (typeof outbound !== 'undefined') {
      outbound.identify(u != null ? u.id.toString() : null, {});
    }
  }

  private registerFeatureFlags(u: ClientUser): void {
    const flags: Flags = {};
    if (u?.features != null) {
      Object.keys(u.features).forEach((key) => {
        flags[key] = u.features[key].current_value === 'true';
      });
    }

    this.featureFlags.register(flags);
  }
}
